import React from "react";
import {Form} from "../../XLibItems";
import {XInputDecimal} from "@michalrakus/x-react-web-lib/XInputDecimal";
import {XInputText} from "@michalrakus/x-react-web-lib/XInputText";
import {XFormFooter} from "@michalrakus/x-react-web-lib/XFormFooter";
import {XInputDate} from "@michalrakus/x-react-web-lib/XInputDate";
import {Utils} from "../../Utils";
import {SluzbaEnum} from "../../common/enums";
import {ZapisPanel} from "./ZapisPanel";
import type {XFormProps} from "@michalrakus/x-react-web-lib/XFormBase";
import {Zapis} from "../../model/zapisy/zapis.entity";
import {XErrors} from "@michalrakus/x-react-web-lib/XErrors";
import {ZapisBaseForm} from "./ZapisBaseForm";
import {XUtilsCommon} from "@michalrakus/x-react-web-lib/XUtilsCommon";
import {XFormHeader} from "@michalrakus/x-react-web-lib/XFormHeader";
import {XFormScrollable} from "../XFormScrollable";
import {XFieldChangeEvent} from "@michalrakus/x-react-web-lib/XFieldChangeEvent";
import {OperationType, XUtils} from "@michalrakus/x-react-web-lib/XUtils";
import {KlientSluzba} from "../../model/klient/klient-sluzba.entity";
import {XAutoComplete} from "@michalrakus/x-react-web-lib/XAutoComplete";
import {dateAsUI, dateFromModel} from "@michalrakus/x-react-web-lib/XUtilsConversions";
import {VykazStreetworkHlavickaForm} from "./VykazStreetworkHlavickaForm";
import {SaveZapisRequest} from "../../common/zapisy-api";
import {VykazStreetworkBrowse} from "./VykazStreetworkBrowse";

@Form("Zapis", true)
export class ZapisForm extends ZapisBaseForm {

    private poznamkaOsetrovnaFromEditStart: string | null = null;
    private klientSluzbaIdFromEditStart: number | null = null;

    // pouzivane v XAutoComplete pre asociaciu vykazStreetwork
    // stary sposob
    //vykazStreetworkList: VykazStreetwork[] = [];

    constructor(props: XFormProps) {
        super(props);

        // najoinujeme vykazStreetwork aj s usermi - potrebujeme ich pri predplnani psych a spec userov, staci pre streetwork
        if (Utils.getCurrentSluzba()?.kod === SluzbaEnum.streetwork) {
            this.addField("vykazStreetwork.vykazXUserList.xUser.id");
        }

        // najoinujeme tieto asociacie (lebo cely ZapisPanel je odprogramovany rucne)
        // najoinujeme zaznam KlientSluzba, Klient a aj obec a stat - atributy na tychto zaznamoch su zobrazene v autocomplete na vyber klienta
        //this.addField("klientSluzba.klient.pohlavie.name");
        this.addField("klientSluzba.klient.obec.nazov");
        this.addField("klientSluzba.klient.stat.nazov");
        // najoinujeme aj klientSluzba.klientSluzbaZakazList - pouziva sa na vycervenenie klienta v autocomplete
        this.addField("klientSluzba.klientSluzbaZakazList.id");
        this.addField("klientSluzba.statusVSluzbe.code"); // pouziva sa na vysedenie klienta v autocomplete
        this.addField("sluzba.kod");
        if (Utils.isSluzbaLujzaPoradenstvo()) {
            this.addField("klientSluzba.lujzaPoradProjekt.name"); // pre autocomplete
            this.addField("lujzaPoradProjekt.name");
        }
        else if (Utils.isSluzbaVincentPoradenstvo()) {
            this.addField("klientSluzba.vincentPoradProjekt.name"); // pre autocomplete
            this.addField("vincentPoradProjekt.name");
        }
        this.addField("psychPoradXUser.name");
        this.addField("specPoradXUser.name");
        this.addField("specPoradXUser2.name");
        this.addField("pracovnik1XUser.name");
        this.addField("pracovnik2XUser.name");

        this.onFieldSetReady = this.onFieldSetReady.bind(this);
        this.callVytvorAtributyHistoria = this.callVytvorAtributyHistoria.bind(this);
        this.onChangeDatum = this.onChangeDatum.bind(this);
        this.onChangeVykazStreetwork = this.onChangeVykazStreetwork.bind(this);
        this.onChangeKlientSluzba = this.onChangeKlientSluzba.bind(this);
    }

    createNewObject(): Zapis {
        return {
            id: undefined!,
            sluzba: Utils.getCurrentSluzba()!,
            vykazStreetwork: null,
            klientSluzba: null,
            datum: !Utils.isSluzbaStreetwork() ? XUtilsCommon.today() : null!,
            vseobecnyZapis: null,
            atributy: {},
            psychPoradCas: null,
            psychPoradXUser: null,
            specPoradCas: null,
            specPoradXUser: null,
            specPoradXUser2: null,
            specPoradZapis: null,
            pracovnik1XUser: Utils.isSluzbaSocPorad() ? Utils.getXUserDePaul() : null, // predplnime pri inserte, ak sa nejedna o osetrovnu
            pracovnik2XUser: null,
            lujzaPoradProjekt: null,
            vincentPoradProjekt: null,
            modifDate: null,
            modifXUser: null,
            lockDate: null,
            lockXUser: null
        };
    }

    preInitForm(object: Zapis, operationType: OperationType.Insert | OperationType.Update) {
        this.klientSluzbaIdFromEditStart = object.klientSluzba?.id ?? null;
        this.saveKlientSluzbaFields(object.klientSluzba);
        //if (Utils.isSluzbaStreetwork()) {
        //    this.loadVykazStreetworkList(object); // this.vykazStreetworkList a object.vykazStreetwork su zavisle od object.datum
        //}
    }

    async componentDidMount() {
        await super.componentDidMount();

        this.setStateXForm(); // aby sa prejavili zmeny ktore zavisia od nacitanych zoznamov

        // metoda vytvorAtributyHistoria() potrebuje mat k dispozicii nacitany zapis (this.state.object) a ostatne data (napr. prava)
        // a tie su nacitane (a set-nute) az ked sa zavola callback v setState
        // predpokladam ze ked sme prisli az sem a zavola sa callback v tomto poslednom setState, tak uz su zapis a ostatne data nacitane
        this.setState({zapisReady: true}, this.callVytvorAtributyHistoria);
    }

    onFieldSetReady() {
        this.setState({fieldSetReady: true}, this.callVytvorAtributyHistoria);
    }

    callVytvorAtributyHistoria() {
        // zavolame this.vytvorAtributyHistoria() ak mame nacitany zapis (this.state.object) a aj metadata v XFieldSetBase (this.state.fieldSetReady je true)
        // nevieme
        if (this.state.zapisReady === true && this.state.fieldSetReady === true) {
            this.vytvorAtributyHistoria();
        }
    }

    /* stary sposob
    async loadVykazStreetworkList(zapis: Zapis) {
        // nacita povolene zaznamy VykazStreetwork (s aktualnym datumom) a kontroluje/nastavuje atribut vykazStreetwork (hlavicka zapisu)
        // ak je aktualny atribut vykazStreetwork z nacitaneho zoznamu VykazStreetwork, tak ho ponechame,
        // ak nie je zo zoznamu (zmenil sa datum a nacitali sa ine zaznamy VykazStreetwork), tak ho nastavime na null alebo ho zmenime na zaznam zo zoznamu, ak ma zoznam presne 1 zaznam
        const datumFilter: Date = dateFromModel(zapis.datum) ?? new Date('1000-01-01'); // ak datum nie je vyplneny, tak neponukneme ziadny zaznam VykazStreetwork (najskor musi vyplnit datum)
        const vykazStreetworkList: VykazStreetwork[] = await XUtils.fetchRows('VykazStreetwork', {where: `[datum] = :datum`, params: {datum: datumFilter}}, "tim.enumOrder", ["tim.name", "vykazXUserList.xUser.name"]);
        if (zapis.vykazStreetwork) {
            if (!XUtilsCommon.arrayIncludes(vykazStreetworkList, zapis.vykazStreetwork, 'id')) {
                // zadana hlavicka nie je medzi povolenymi
                if (vykazStreetworkList.length === 1) {
                    zapis.vykazStreetwork = vykazStreetworkList[0];
                }
                else {
                    zapis.vykazStreetwork = null;
                }
            }
        }
        else {
            // hlavicka nie je zadana - predplnime ak sa da
            if (vykazStreetworkList.length === 1) {
                zapis.vykazStreetwork = vykazStreetworkList[0];
            }
        }
        this.vykazStreetworkList = vykazStreetworkList; // pouzijeme v XAutoComplete
    }
     */

    private saveKlientSluzbaFields(klientSluzba: KlientSluzba | null) {
        // ulozime si hodnoty fieldov z entity KlientSluzba zo zaciatku editacie - ak user zmeni hodnotu, zapiseme si modifDate a modifXUser do zaznamu klient
        this.poznamkaOsetrovnaFromEditStart = klientSluzba?.poznamkaOsetrovna ?? null;
    }

    async vytvorAtributyHistoria() {
        const zapis: Zapis | null = this.getZapis();
        if (!zapis) {
            throw "Unexpected error - zapis is null";
        }
        await ZapisPanel.vytvorAtributyHistoria(zapis);

        this.setStateXForm(); // aby sa prejavili zmeny ktore zavisia od nacitanych zoznamov
    }

    // pomocna metodka
    private getZapis(): Zapis | null {
        return this.state.object;
    }

    async onChangeDatum(e: XFieldChangeEvent<Zapis>) {
        // zmenil sa datum, skontrolujeme/nastavime atribut vykazStreetwork
        //if (Utils.isSluzbaStreetwork()) {
        //    await this.loadVykazStreetworkList(e.object);
        //}
        // zmenil sa datum, prepocitame historiu atributov
        await ZapisPanel.vytvorAtributyHistoria(e.object);
        // kedze sme pouzili await, tak musime zavolat setState (framework necaka a vola setState hned)
        this.setStateXForm();
    }

    async onChangeVykazStreetwork(e: XFieldChangeEvent<Zapis>) {
        // skopirujeme datum
        if (e.object.vykazStreetwork) {
            e.object.datum = e.object.vykazStreetwork.datum;

            // ak sme v inserte a nemame vyplneneho pracovnika, tak ho predvyplnime na aktualneho usera (ak sa nachadza na denniku)
            // je to take oneskorene predplnenie (podobne je v metode ZapisBrowse.onAddRow)
            if (this.isAddRow() && e.object.pracovnik1XUser === null) {
                e.object.pracovnik1XUser = Utils.streetworkZapisPracovnikForInit(e.object.vykazStreetwork);
            }
        }

        // mohol sa zmenit datum, prepocitame historiu atributov
        await ZapisPanel.vytvorAtributyHistoria(e.object);
        // kedze sme pouzili await, tak musime zavolat setState (framework necaka a vola setState hned)
        this.setStateXForm();
    }

    onChangeKlientSluzba(klientSluzba: KlientSluzba | null) {
        this.saveKlientSluzbaFields(klientSluzba);
    }

    // overrides method in XFormBase
    async validate(zapis: Zapis): Promise<XErrors> {
        const errors: XErrors = ZapisPanel.validateZapis(zapis.vykazStreetwork, zapis, this.state.kodSluzbaRolaPravaMap);
        // ak vytvorime hlavicku cez plus button, moze vzniknut rozdielny datum
        if (zapis.vykazStreetwork) {
            if (zapis.datum) {
                if (!XUtilsCommon.dateEquals(dateFromModel(zapis.datum), dateFromModel(zapis.vykazStreetwork.datum))) {
                    errors.vykazStreetwork = `Dátum denníku (${dateAsUI(dateFromModel(zapis.vykazStreetwork.datum))}) musí byť rovnaký ako dátum zápisu (${dateAsUI(dateFromModel(zapis.datum))}).`;
                }
            }
        }
        return errors;
    }

    // preSave(object: Zapis) {
    //     super.preSave(object);
    //
    //     object.atributyHistoria = undefined; // info o historii atributov nechceme posielat na backend
    //     object.xFieldSetBaseRef = undefined; // referenciu xFieldSetBaseRef nechceme posielat na backend
    // }

    // overridneme standardny saveRow
    async saveRow(): Promise<void> {
        // pouzivame custom service lebo potrebujeme zapisat aj zaznam klientSluzba

        const zapis: Zapis = this.getZapis()!;

        // ak sa zmenil nejaky atribut z entity klientSluzba, tak zapiseme aj modif atributy (viac-menej kozmeticka zalezitost)
        let klientSluzbaChanged: boolean = false;
        if (zapis.klientSluzba && zapis.klientSluzba.poznamkaOsetrovna !== this.poznamkaOsetrovnaFromEditStart) {
            zapis.klientSluzba.modifDate = new Date();
            zapis.klientSluzba.modifXUser = XUtils.getXToken()?.xUser;
            klientSluzbaChanged = true;
        }

        // pre Alzbeta osetrovna vykonavame na backend-e test ci zapis s danym datumom a klientom uz existuje
        // ak test neprejde, nesmieme mat zmazane atributyHistoria a xFieldSetBaseRef - preto ich mazeme na kopii
        const zapisCopy: Zapis = {...zapis};
        zapisCopy.atributyHistoria = undefined; // info o historii atributov nechceme posielat na backend
        zapisCopy.xFieldSetBaseRef = undefined; // referenciu xFieldSetBaseRef nechceme posielat na backend
        const saveZapisRequest: SaveZapisRequest = {zapis: zapisCopy, klientSluzbaChanged: klientSluzbaChanged, klientSluzbaIdPrev: this.klientSluzbaIdFromEditStart};
        await XUtils.post('save-zapis', saveZapisRequest);
    }

    renderDesktop() {
        // maxWidth="128rem" je esteticka zalezitost, aby formular nebol roztiahnuty na celu sirku displaya na velmi sirokych displayoch
        // (menej ako 128 zas sposobuje wrapovanie na niektorych group fieldoch ktore maju vela checkboxov)
        return (
            <div>
                <XFormScrollable form={this} headerFooterHeight="8rem" widthFitContent={true} maxWidth="128rem">
                    <div className="x-form-row">
                        <div className="x-form-col">
                            <div className="x-form-row justify-content-between">
                                <div className="x-form-inline-row">
                                    {/* datum zobrazime len ak neni streetwork alebo mame zaznam z migracie bez dennika */}
                                    {!Utils.isSluzbaStreetwork() || (this.getZapis()?.vykazStreetwork === null && !this.isAddRow()) ?
                                        <XInputDate form={this} field="datum" label="Dátum" labelStyle={{width:'6rem'}} readOnly={Utils.isSluzbaStreetwork()} onChange={this.onChangeDatum}/> : null}
                                    {Utils.isSluzbaStreetwork() ?
                                        <XAutoComplete form={this} assocField="vykazStreetwork" label="Denník *" labelStyle={{width:'6rem'}} width="27rem"
                                                       displayField={["datum", "vykazXUserList.xUser.name"]}
                                                       sortField={[{field: "datum", order: -1}, {field: "id", order: -1}]}
                                                       searchBrowse={<VykazStreetworkBrowse/>} assocForm={<VykazStreetworkHlavickaForm/>}
                                                       insertButtonTooltip="Nový denník" updateButtonTooltip="Upraviť denník"
                                                       scrollHeight="50rem"
                                                       suggestionsLoad="lazy" lazyLoadMaxRows={30} minLength={2}
                                                       onChange={this.onChangeVykazStreetwork}/> : null}
                                </div>
                                <XFormHeader form={this} label={Utils.isSluzbaNoclaharen() ? "Ošetrenie" : "Zápis"} style={{marginTop: '0rem', marginBottom: '1rem'}}/>
                                <div className="x-form-inline-row">
                                    <XInputDate form={this} field="modifDate" label="Modif." labelStyle={{width:'4.5rem'}} readOnly={true}/>
                                    <XInputText form={this} field="modifXUser.name" labelStyle={{width:'0rem'}} inputStyle={{width:'10rem'}} readOnly={true}/>
                                    {!this.isAddRow() ? <XInputDecimal form={this} field="id" labelStyle={{width:'0rem'}} inputStyle={{width:'5rem'}} readOnly={true}/> : null}
                                </div>
                            </div>
                            <ZapisPanel zapisForm={true} vykazStreetwork={this.state.object?.vykazStreetwork}
                                        zapis={this.state.object} onChangeZapis={this.onChangeForm}
                                        onChangeKlientSluzba={this.onChangeKlientSluzba}
                                        userSpecPoradList={this.userSpecPoradList}
                                        userPsychPoradList={this.userPsychPoradList}
                                        userList={this.userList} readOnly={this.formReadOnlyBase("zapisPanel")}
                                        idSluzbaRolaPravaMap={this.state.idSluzbaRolaPravaMap}
                                        kodSluzbaRolaPravaMap={this.state.kodSluzbaRolaPravaMap}
                                        onEditFieldChange={this.onEditFieldChange}
                                        onFieldSetReady={this.onFieldSetReady}/>
                        </div>
                    </div>
                </XFormScrollable>
                <XFormFooter form={this}/>
            </div>
        );
    }

    // TODO - bolo by fajn pridat label + tlacitko na zobrazenie modif atributov (vedla datumu)
    renderMobile() {
        return (
            <div>
                <XFormScrollable form={this} headerFooterHeight="8rem" widthFitContent={true}>
                    <div className="x-form-row">
                        <div className="x-form-col">
                            <div style={{height:'0.5rem'}}/>
                            {/* datum zobrazime len ak neni streetwork alebo mame zaznam z migracie bez dennika */}
                            {!Utils.isSluzbaStreetwork() || (this.getZapis()?.vykazStreetwork === null && !this.isAddRow()) ?
                                <XInputDate form={this} field="datum" label="Dátum" labelStyle={{width:'6rem'}} readOnly={Utils.isSluzbaStreetwork()} onChange={this.onChangeDatum}/> : null}
                            {Utils.isSluzbaStreetwork() ?
                                <XAutoComplete form={this} assocField="vykazStreetwork" label="Denník *" labelStyle={{width:'6rem'}} width="calc(100% - 6.5rem)"
                                               displayField={["datum", "vykazXUserList.xUser.name"]}
                                               sortField={[{field: "datum", order: -1}, {field: "id", order: -1}]}
                                               searchBrowse={<VykazStreetworkBrowse/>} assocForm={<VykazStreetworkHlavickaForm/>}
                                               insertButtonTooltip="Nový denník" updateButtonTooltip="Upraviť denník"
                                               scrollHeight="50rem"
                                               suggestionsLoad="lazy" lazyLoadMaxRows={30} minLength={2}
                                               onChange={this.onChangeVykazStreetwork}/> : null}
                            <ZapisPanel zapisForm={true} vykazStreetwork={this.state.object?.vykazStreetwork}
                                        zapis={this.state.object} onChangeZapis={this.onChangeForm}
                                        onChangeKlientSluzba={this.onChangeKlientSluzba}
                                        userSpecPoradList={this.userSpecPoradList}
                                        userPsychPoradList={this.userPsychPoradList}
                                        userList={this.userList} readOnly={this.formReadOnlyBase("zapisPanel")}
                                        idSluzbaRolaPravaMap={this.state.idSluzbaRolaPravaMap}
                                        kodSluzbaRolaPravaMap={this.state.kodSluzbaRolaPravaMap}
                                        onEditFieldChange={this.onEditFieldChange}
                                        onFieldSetReady={this.onFieldSetReady}/>
                        </div>
                    </div>
                </XFormScrollable>
                <XFormFooter form={this}/>
            </div>
        );
    }

    render() {
        if (!XUtils.isMobile()) {
            return this.renderDesktop();
        } else {
            return this.renderMobile();
        }
    }
}
